import { Fragment } from 'react';
import { Field, FieldArray, Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import ToastWrapper from './ToastWrapper';
import { useToastMessage } from '../hooks/useToastMessage';
import { RouteCard, RouteCardOperationsUpdateResponse, ToastType } from '../interfaces';
import { patchRouteCardOperations } from '../api/patchRouteCardOperations';

const COMPANY_NAME = 'ТОВ "Оскар Тех"';

export interface EditRouteCardDialogProps {
    open: boolean;
    routeCard: RouteCard;
    onClose: () => void;
    onSuccess: (result: RouteCardOperationsUpdateResponse) => void;
}

interface RouteCardOperationFormValue {
    operationId: string;
    name: string;
    section: string | null;
    notes: string | null;
    requiresFirmwareValue: boolean;
    expectedFirmwareValue: string | null;
    completed: boolean;
    completedBy: string | null;
    firmwareValue: string;
}

interface FormValues {
    operations: RouteCardOperationFormValue[];
}

const formatRouteCardDate = (value: string) => {
    const parsed = DateTime.fromISO(value, { zone: 'utc' });
    return parsed.isValid ? parsed.setZone('Europe/Kyiv').toFormat('dd.MM.yyyy HH:mm') : value;
};

const getFirmwareValueColor = (value: string, expected: string | null): string | undefined => {
    if (!expected || !value) {
        return undefined;
    }

    return value.trim() === expected.trim() ? 'success.main' : 'error.main';
};

export const EditRouteCardDialog = ({ open, routeCard, onClose, onSuccess }: EditRouteCardDialogProps) => {
    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();
    const isReadOnly = routeCard.status === 'completed';

    const initialValues: FormValues = {
        operations: routeCard.operations
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((operation) => ({
                operationId: operation.operationId,
                name: operation.name,
                section: operation.section,
                notes: operation.notes,
                requiresFirmwareValue: operation.requiresFirmwareValue,
                expectedFirmwareValue: operation.expectedFirmwareValue,
                completed: operation.completed,
                completedBy: operation.completedBy,
                firmwareValue: operation.firmwareValue || '',
            })),
    };

    const validateForm = (values: FormValues) => {
        const errors: any = {};

        const operationErrors: Record<number, any> = {};
        values.operations.forEach((operation, index) => {
            if (operation.completed && operation.requiresFirmwareValue && !operation.firmwareValue) {
                operationErrors[index] = { firmwareValue: 'Required when Completed is checked' };
            }
        });

        if (Object.keys(operationErrors).length > 0) {
            errors.operations = operationErrors;
        }

        return errors;
    };

    const handleSubmit = async (values: FormValues, { setSubmitting }: any) => {
        try {
            const result = await patchRouteCardOperations(routeCard.id, {
                operations: values.operations.map((operation) => ({
                    operationId: operation.operationId,
                    completed: operation.completed,
                    firmwareValue: operation.firmwareValue || undefined,
                })),
            });

            onSuccess(result);
        } catch (error) {
            console.error(error);
            setToastMessage({
                message: 'Під час збереження маршрутної карти відбулася помилка',
                type: ToastType.WARNING,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Маршрутна карта — {routeCard.deviceId}</DialogTitle>
            <DialogContent>
                <Formik validate={validateForm} onSubmit={handleSubmit} initialValues={initialValues}>
                    {({ values, errors, isSubmitting }) => (
                        <Form>
                            <Box sx={{ marginTop: 1, marginBottom: 2 }}>
                                <TableContainer component={Paper} variant="outlined" sx={{ marginBottom: 2 }}>
                                    <Table size="small">
                                        <TableBody>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>
                                                    {COMPANY_NAME}
                                                </TableCell>
                                                <TableCell>
                                                    Маршрутна карта виготовлення виробу «
                                                    {routeCard.templateName}»
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={2}>
                                                    Серійний номер пристрою: {routeCard.deviceId}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={2}>
                                                    Дата: {formatRouteCardDate(routeCard.createdAt)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    Назва операції
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                                    Виконано
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Виконав</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    Прошивка
                                                    <br />
                                                    (тільки для відладки)
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Примітки</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <FieldArray name="operations">
                                            {() => (
                                                <TableBody>
                                                    {values.operations.map((operation, index) => {
                                                        const showSectionHeader =
                                                            operation.section &&
                                                            operation.section !==
                                                                values.operations[index - 1]?.section;
                                                        const operationError = (errors.operations as any)?.[
                                                            index
                                                        ];
                                                        const firmwareColor = getFirmwareValueColor(
                                                            operation.firmwareValue,
                                                            operation.expectedFirmwareValue,
                                                        );

                                                        return (
                                                            <Fragment key={operation.operationId}>
                                                                {showSectionHeader && (
                                                                    <TableRow>
                                                                        <TableCell
                                                                            colSpan={5}
                                                                            sx={{ fontWeight: 'bold' }}
                                                                        >
                                                                            {operation.section}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                                <TableRow>
                                                                    <TableCell>{operation.name}</TableCell>
                                                                    <TableCell align="center">
                                                                        <Field
                                                                            as={Checkbox}
                                                                            type="checkbox"
                                                                            name={`operations[${index}].completed`}
                                                                            size="small"
                                                                            disabled={isReadOnly}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {operation.completedBy || '—'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {operation.requiresFirmwareValue ? (
                                                                            <>
                                                                                <Field
                                                                                    as={TextField}
                                                                                    name={`operations[${index}].firmwareValue`}
                                                                                    variant="standard"
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    disabled={isReadOnly}
                                                                                    error={Boolean(
                                                                                        operationError?.firmwareValue,
                                                                                    )}
                                                                                    helperText={
                                                                                        operationError?.firmwareValue
                                                                                    }
                                                                                    sx={{
                                                                                        '& .MuiInputBase-input':
                                                                                            {
                                                                                                color: firmwareColor,
                                                                                            },
                                                                                    }}
                                                                                />
                                                                                {operation.expectedFirmwareValue && (
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        component="div"
                                                                                        sx={{
                                                                                            color:
                                                                                                firmwareColor ||
                                                                                                'text.secondary',
                                                                                        }}
                                                                                    >
                                                                                        Очікується:{' '}
                                                                                        {
                                                                                            operation.expectedFirmwareValue
                                                                                        }
                                                                                    </Typography>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            '—'
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {operation.notes || '—'}
                                                                    </TableCell>
                                                                </TableRow>
                                                            </Fragment>
                                                        );
                                                    })}
                                                </TableBody>
                                            )}
                                        </FieldArray>
                                    </Table>
                                </TableContainer>

                                {routeCard.instructions && (
                                    <Typography variant="body2" sx={{ marginTop: 2 }}>
                                        {routeCard.instructions}
                                    </Typography>
                                )}

                                <DialogActions>
                                    <Button size="small" variant="outlined" onClick={onClose} color="error">
                                        Cancel
                                    </Button>
                                    {!isReadOnly && (
                                        <Button
                                            type="submit"
                                            size="small"
                                            variant="outlined"
                                            disabled={isSubmitting}
                                            color="primary"
                                        >
                                            Save
                                            {isSubmitting && (
                                                <CircularProgress style={{ marginLeft: '5px' }} size={15} />
                                            )}
                                        </Button>
                                    )}
                                </DialogActions>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
            <ToastWrapper
                open={isOpened}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={3000}
                onClose={handleCloseToast}
                message={toastMessage?.message}
                type={toastMessage?.type}
            />
        </Dialog>
    );
};
